import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NoodleCup from "../components/NoodleCup";
import VictoryCard from "../components/VictoryCard";
import {
  DIFFICULTIES,
  TEAMS,
  ROUNDS_PER_MATCH,
  ROUNDS_TO_WIN,
  randomDelayMs,
  sampleAiTime,
  verdict,
} from "../lib/game";
import { usePlayer } from "../context/PlayerContext";
import { postMatch } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { playSplat, playDraw, playWin, playLose, playFoul, playTick } from "../lib/audio";

const PHASES = { READY: "ready", WAIT: "wait", DRAW: "draw", RESULT: "result", MATCH_END: "match_end" };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

const Duel = () => {
  const { difficulty } = useParams();
  const navigate = useNavigate();
  const { username, team } = usePlayer();
  const cfg = DIFFICULTIES[difficulty];
  const playerTeam = team && TEAMS[team] ? team : "chicken";
  const enemyTeam = playerTeam === "beef" ? "chicken" : "beef";
  const isMobile = useIsMobile();

  const [phase, setPhase] = useState(PHASES.READY);
  const [roundIdx, setRoundIdx] = useState(0);
  const [rounds, setRounds] = useState([]);
  const [drawAt, setDrawAt] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(false);

  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!username) navigate("/");
    if (!cfg) navigate("/lobby");
    // Preload projectile cup renders so first-round animation renders cleanly
    ["/brand/cup-beef-render.png", "/brand/cup-chicken-render.png"].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    // eslint-disable-next-line
  }, []);

  const wins = rounds.filter((r) => r.win).length;
  const losses = rounds.filter((r) => !r.win).length;

  const startRound = useCallback(() => {
    setLastResult(null);
    setPhase(PHASES.WAIT);
    setDrawAt(null);
    const delay = randomDelayMs();
    timeoutRef.current = setTimeout(() => {
      setFlash(true);
      setTimeout(() => setFlash(false), 360);
      setDrawAt(performance.now());
      setPhase(PHASES.DRAW);
      playDraw();
      timeoutRef.current = setTimeout(() => finishRound(null, true), 2500);
    }, delay);
  }, []); // eslint-disable-line

  const finishRound = (playerMs, missed = false) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const aiMs = sampleAiTime(difficulty);
    const foul = playerMs !== null && playerMs < 0;
    const safePlayer = foul || missed ? null : playerMs;
    const v = verdict(safePlayer, aiMs, foul);
    const round = { player_time_ms: safePlayer, ai_time_ms: aiMs, foul, win: v.win };
    setRounds((prev) => [...prev, round]);
    setLastResult({ ...round, label: v.label });
    setPhase(PHASES.RESULT);
    if (foul) playFoul();
    else { playSplat(); if (v.win) playWin(); else playLose(); }
  };

  const handleClick = () => {
    if (phase === PHASES.WAIT) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      finishRound(-1);
      return;
    }
    if (phase === PHASES.DRAW && drawAt) {
      const ms = Math.round(performance.now() - drawAt);
      finishRound(ms);
    }
  };

  // Spacebar input
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.key === " ") {
        if (phase === PHASES.WAIT || phase === PHASES.DRAW) {
          e.preventDefault();
          handleClick();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [phase, drawAt]);

  const nextRound = async () => {
    const newIdx = roundIdx + 1;
    const playerWins = rounds.filter((r) => r.win).length;
    const aiWins = rounds.length - playerWins;
    const matchOver =
      newIdx >= ROUNDS_PER_MATCH || playerWins >= ROUNDS_TO_WIN || aiWins >= ROUNDS_TO_WIN;

    if (matchOver) {
      setPhase(PHASES.MATCH_END);
      setSubmitting(true);
      try {
        await postMatch({ username, difficulty, rounds, won: playerWins > aiWins });
      } catch {
        toast.error("Couldn't wire results back to base.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setRoundIdx(newIdx);
    startRound();
  };

  useEffect(() => {
    if (phase === PHASES.READY && rounds.length === 0 && roundIdx === 0) {
      const t = setTimeout(() => startRound(), 1200);
      return () => clearTimeout(t);
    }
  }, [phase, rounds.length, roundIdx, startRound]);

  const playAgain = () => {
    setRounds([]);
    setRoundIdx(0);
    setLastResult(null);
    setPhase(PHASES.READY);
  };

  const finalPlayerWins = rounds.filter((r) => r.win).length;
  const finalAiWins = rounds.length - finalPlayerWins;
  const matchWon = finalPlayerWins > finalAiWins;
  const validTimes = rounds.map((r) => r.player_time_ms).filter((t) => t != null);
  const bestTime = validTimes.length ? Math.min(...validTimes) : null;
  const avgTime = validTimes.length
    ? Math.round(validTimes.reduce((a, b) => a + b, 0) / validTimes.length)
    : null;

  const projectileKey = phase === PHASES.DRAW || phase === PHASES.RESULT ? roundIdx : -1;

  return (
    <div className="bg-fight min-h-screen relative overflow-hidden">
      {flash && <div className="flash-overlay" />}

      {/* projectiles - real cup renders flying */}
      {(phase === PHASES.DRAW || phase === PHASES.RESULT) && lastResult && !lastResult.foul && (
        <>
          {lastResult.player_time_ms != null && (
            <img
              key={`pn-${projectileKey}`}
              src={TEAMS[playerTeam].render}
              alt=""
              className="noodle-projectile"
              style={{
                left: "22%",
                top: "48%",
                width: isMobile ? 80 : 140,
                height: "auto",
                animation: "noodle-fly-right 600ms ease-out forwards",
              }}
            />
          )}
          <img
            key={`an-${projectileKey}`}
            src={TEAMS[enemyTeam].render}
            alt=""
            className="noodle-projectile"
            style={{
              right: "22%",
              top: "48%",
              width: isMobile ? 80 : 140,
              height: "auto",
              animation: "noodle-fly-left 600ms ease-out forwards",
            }}
          />
        </>
      )}

      <div
        className="min-h-screen flex flex-col select-none relative"
        onMouseDown={(e) => {
          if (e.target.closest("button[data-no-duel-click]")) return;
          if (phase === PHASES.WAIT || phase === PHASES.DRAW) handleClick();
        }}
        data-testid="duel-stage"
      >
        {/* HUD top */}
        <div className="px-5 sm:px-12 pt-6 flex items-center justify-between relative z-20">
          <button
            data-no-duel-click
            data-testid="duel-quit-button"
            onClick={() => navigate("/lobby")}
            className="font-elite text-sm flex items-center gap-2 text-[color:var(--deepest-brown)] hover:text-[color:var(--red)]"
          >
            <ArrowLeft size={16} /> retreat
          </button>
          <div
            className="font-anton text-2xl sm:text-3xl tracking-widest"
            style={{ color: "var(--red)", textShadow: "2px 2px 0 var(--yellow), 3px 3px 0 var(--deepest-brown)" }}
          >
            ROUND {Math.min(roundIdx + 1, ROUNDS_PER_MATCH)}/{ROUNDS_PER_MATCH}
          </div>
          <div className="font-elite text-xs sm:text-sm text-[color:var(--deepest-brown)] capitalize">
            {cfg?.label} · the foe
          </div>
        </div>

        {/* score bar */}
        <div className="px-5 sm:px-12 mt-4 grid grid-cols-2 gap-4 relative z-20">
          <div className="flex items-center gap-2 font-anton text-[color:var(--deepest-brown)]">
            <span className="text-sm tracking-widest">YOU · {TEAMS[playerTeam].label.toUpperCase()}</span>
            <div className="flex gap-1">
              {Array.from({ length: ROUNDS_TO_WIN }).map((_, i) => (
                <span key={i} className={`w-4 h-4 border-2 border-[color:var(--deepest-brown)] ${i < wins ? "bg-[color:var(--red)]" : "bg-transparent"}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 font-anton text-[color:var(--deepest-brown)] justify-end">
            <div className="flex gap-1">
              {Array.from({ length: ROUNDS_TO_WIN }).map((_, i) => (
                <span key={i} className={`w-4 h-4 border-2 border-[color:var(--deepest-brown)] ${i < losses ? "bg-[color:var(--deepest-brown)]" : "bg-transparent"}`} />
              ))}
            </div>
            <span className="text-sm tracking-widest">FOE</span>
          </div>
        </div>

        {/* arena */}
        <div className="flex-1 flex flex-col items-center justify-center relative px-4">
          <div className="w-full max-w-6xl flex items-end justify-between gap-2 sm:gap-0">
            <NoodleCup
              variant={playerTeam}
              size={isMobile ? 150 : 360}
              state={
                phase === PHASES.MATCH_END
                  ? matchWon ? "win" : "ko"
                  : phase === PHASES.RESULT
                    ? lastResult?.win ? "win" : "ko"
                    : phase === PHASES.DRAW ? "draw" : "idle"
              }
              testid="player-mascot"
            />
            <NoodleCup
              variant={enemyTeam}
              size={isMobile ? 150 : 360}
              state={
                phase === PHASES.MATCH_END
                  ? matchWon ? "ko" : "win"
                  : phase === PHASES.RESULT
                    ? lastResult?.win ? "ko" : "win"
                    : phase === PHASES.DRAW ? "draw" : "idle"
              }
              testid="enemy-mascot"
            />
          </div>

          {/* center text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {phase === PHASES.READY && (
              <div className="text-center">
                <div className="font-elite text-xs tracking-[0.3em]">GET READY...</div>
                <div className="font-display text-5xl mt-2 title-3d-red">HOLD STEADY</div>
              </div>
            )}
            {phase === PHASES.WAIT && (
              <div className="font-display text-5xl sm:text-7xl pulse-soft title-3d-red">
                WAIT FOR IT...
              </div>
            )}
            {phase === PHASES.DRAW && (
              <div data-testid="draw-text" className="text-center">
                <div className="font-display text-7xl sm:text-9xl title-3d-yellow draw-slam">
                  SLURP!
                </div>
                <div className="font-elite text-base sm:text-lg text-[color:var(--bone)] mt-2" data-testid="draw-hint">
                  {isMobile ? "Tap the screen" : "Click or hit SPACE"}
                </div>
              </div>
            )}
            {phase === PHASES.RESULT && lastResult && (
              <div
                className="text-center pointer-events-auto bg-[color:var(--bone)] border-4 border-[color:var(--deepest-brown)] p-5 sm:p-7"
                style={{ boxShadow: "6px 6px 0 0 rgba(58,29,4,0.45)" }}
              >
                <div
                  className={`font-display text-4xl sm:text-6xl ${lastResult.win ? "title-3d-red" : ""}`}
                  style={!lastResult.win ? { color: "var(--deepest-brown)" } : {}}
                  data-testid="round-result-label"
                >
                  {lastResult.label}
                </div>
                <div className="font-elite text-sm mt-3 text-[color:var(--deepest-brown)]">
                  {lastResult.foul
                    ? "You smoked yourself."
                    : lastResult.player_time_ms == null
                      ? "Y'never even slurped."
                      : `you: ${lastResult.player_time_ms}ms · foe: ${lastResult.ai_time_ms}ms`}
                </div>
                <button
                  data-no-duel-click
                  data-testid="next-round-button"
                  onClick={() => { playTick(); nextRound(); }}
                  className="btn-logo mt-5"
                >
                  <span>{(roundIdx + 1 >= ROUNDS_PER_MATCH || wins >= ROUNDS_TO_WIN || losses >= ROUNDS_TO_WIN) ? "End Showdown" : "Next Round"}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* match end overlay */}
      {phase === PHASES.MATCH_END && (
        <div className="fixed inset-0 z-40 bg-black/75 overflow-y-auto" data-testid="match-end-screen">
          <div className="min-h-full flex items-center justify-center p-4 py-6">
            <div className="poster max-w-4xl w-full p-5 sm:p-7">
            <div className="text-center">
              <div className="font-elite text-xs tracking-[0.3em] text-[color:var(--red)]">— THE VERDICT —</div>
              <div className={`font-display text-4xl sm:text-5xl mt-2 ${matchWon ? "title-3d-red" : ""}`} style={!matchWon ? { color: "var(--deepest-brown)" } : {}}>
                {matchWon ? "YOU SAUCED 'EM!" : "SMOKED"}
              </div>
              <div className="font-elite text-sm mt-2 text-[color:var(--deepest-brown)]">
                {matchWon
                  ? `The ${cfg.label} hit the dirt. Your reputation grows saucier.`
                  : `The ${cfg.label} sent ya home. Best dust off and try again.`}
              </div>
            </div>

            <div className="mt-5 flex justify-center" data-testid="victory-card-wrapper">
              <VictoryCard
                username={username}
                playerTeam={playerTeam}
                won={matchWon}
                foeLabel={cfg.label}
                score={`${finalPlayerWins}-${finalAiWins}`}
                bestMs={bestTime}
                avgMs={avgTime}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="play-again-button" onClick={() => { playTick(); playAgain(); }} className="btn-logo btn-cta-anim" disabled={submitting}>
                <span>Reload &amp; Rematch</span>
              </button>
              <button data-testid="goto-lobby-button" onClick={() => { playTick(); navigate("/lobby"); }} className="btn-fantastic yellow">
                <span>Back to Saloon</span>
              </button>
              <button data-testid="goto-dashboard-button" onClick={() => { playTick(); navigate("/dashboard"); }} className="btn-fantastic yellow">
                <span>See Ledger</span>
              </button>
            </div>
            <div className="mt-3 text-center font-anton text-sm" data-testid="final-score">
              FINAL · {finalPlayerWins}–{finalAiWins}
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Duel;
